import React, { useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Select,
  Input,
  Button,
  Table,
  Typography,
  Space,
  Tooltip,
  Modal,
  Tabs
} from 'antd';
import {
  EditFilled,
  DeleteFilled,
  SearchOutlined,
  PlusOutlined,
  UploadOutlined,
  DownloadOutlined,
  EyeFilled,
  CheckCircleFilled,
  CloseCircleOutlined,
  CloseCircleFilled,
  ClearOutlined
} from '@ant-design/icons';
import BatteryGauge from 'react-battery-gauge';
import { apiGetDevicesInformation } from '@services/ManageDevices/DeviceServices';
import { showError, showWarning } from '@components/notification';
import { communityList, downloadBlobFile } from '@utils/utils';
import StatCards from './StatCards';
import { useNavigate } from 'react-router-dom';
import ModalImportFile from './ModalImportFile';
import { UploadFileOutlined } from '@mui/icons-material';
import { URL_API } from '@configs/index';
import moment from 'moment';
import { apiDeleteCustomer, apiGetAllCustomer } from '@services/ManageCustomers/CustomerServices';
import { usePermission } from '@contexts/Auth/UsePermission';
import { loadFromLocalStorageObjectFromBase64 } from '@databases/localStorage';
import { contantAuthentication } from '@contants/index';
import TabPane from 'antd/es/tabs/TabPane';
import BranchSelectMultiple from '@components/FilterAdvance/components/SelectBranchMultiple';
import ManageGroupSelect from '@components/FilterAdvance/components/SelectManageGroup';
import ParentDeviceSelect from '@components/FilterAdvance/components/SelectParentDevice';
import { SelectRoute } from '@components/Select/SelectRoute';
import RouteSelect from '@components/FilterAdvance/components/SelectRoute';
import MeterSizeSelect from '@components/FilterAdvance/components/SelectMeterSize';

const { Option } = Select;
const { Title, Paragraph } = Typography;

export default function List() {
  const [selectedCommunity, setSelectedCommunity] = useState('ALL');
  const [deviceList, setDeviceList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [flagSearch, setFlagSearch] = useState(false);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const navigate = useNavigate();
  const [visibleModalImport, setVisibleModalImport] = useState(false);
  const user = JSON.parse(loadFromLocalStorageObjectFromBase64(contantAuthentication.DATA_AUTH));
  const { customer } = usePermission();
  const [isFetching, setIsFetching] = useState(false);
  const [status, setStatus] = useState('all');
  const [dataFilterAdvance, setDataFilterAdvance] = useState({});

  // Check and apply filter from localStorage (saved from FavoriteFilters page)
  useEffect(() => {
    const savedFilter = localStorage.getItem('appliedFavoriteFilter');
    if (savedFilter) {
      try {
        const filterData = JSON.parse(savedFilter);
        const { filter_type, item_ids, filter_name } = filterData;

        if (filter_type === 'customer' && item_ids && item_ids.length > 0) {
          // Filter by customer IDs
          setDataFilterAdvance({
            customer_ids: item_ids
          });
        } else if (filter_type === 'device' && item_ids && item_ids.length > 0) {
          // Filter by device IDs
          setDataFilterAdvance({
            device_ids: item_ids
          });
        }

        setCurrentPage(1);
        setPageSize(10);
        setFlagSearch((prev) => !prev);

        // Clear the stored filter
        localStorage.removeItem('appliedFavoriteFilter');
      } catch (err) {
        console.error('Error applying saved filter:', err);
        localStorage.removeItem('appliedFavoriteFilter');
      }
    }
  }, []);

  const fetchData = async () => {
    try {
      setIsFetching(true);
      const res = await apiGetAllCustomer({
        page: currentPage,
        size: pageSize,
        keyWord: search ?? undefined,
        status: status === 'all' ? undefined : status,
        ...dataFilterAdvance
        // community: selectedCommunity === 'ALL' ? undefined : selectedCommunity
      });
      setDeviceList(
        res.data?.data?.map((item, index) => ({
          ...item,
          stt: (currentPage - 1) * pageSize + 1 + index
        }))
      );
      setTotalItems(res.data?.meta?.total);
    } catch (err) {
      showError('Something went wrong!');
      console.log(err);
    } finally {
      setIsFetching(false);
    }
  };

  const handleEdit = (record) => {
    navigate(`/customer/${record.id}`);
  };

  const handleViewDetail = (record) => {
    navigate(`/customer/detail/${record.id}`);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Bạn có chắc chắn muốn xoá Khách hàng',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        await apiDeleteCustomer(record?.id);
        fetchData();
      },
      onCancel() {
        console.log('Cancel deletion');
      }
    });
  };

  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     if (!search) {
  //       setCurrentPage(1);
  //       fetchData();
  //     }
  //   }, 10000);

  //   return () => clearInterval(interval);
  // }, [search, selectedCommunity]);

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, flagSearch, selectedCommunity, status]);

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleChangePageSize = (current, size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const columns = useMemo(
    () => [
      {
        title: 'STT',
        dataIndex: 'stt',
        width: 70,
        fixed: 'left',
        align: 'center'
      },
      {
        title: 'Trạng thái',
        dataIndex: 'status',
        fixed: 'left',
        render: (text, record) => {
          return (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {record?.status === 'IN_USE' ? (
                  <CheckCircleFilled style={{ fontSize: '16px', color: '#52c41a' }} />
                ) : (
                  <CloseCircleFilled style={{ fontSize: '16px', color: '#5D5D5D' }} />
                )}
                <Typography.Text
                  style={{
                    fontSize: '12px',
                    color: record?.status === 'IN_USE' ? '#52c41a' : '#ff4d4f'
                  }}>
                  {record?.status === 'IN_USE'
                    ? 'Đang lắp đặt thiết bị'
                    : 'Không lắp đặt thiết bị '}
                </Typography.Text>
              </div>
            </>
          );
        },
        width: 120
      },
      {
        title: 'DBĐD',
        dataIndex: 'code',
        fixed: 'left',
        width: 100
      },
      {
        title: 'Tên KH',
        dataIndex: 'name',
        fixed: 'left',
        width: 200
      },
      {
        title: 'Địa chỉ',
        dataIndex: 'address',
        width: 200
      },
      {
        title: 'Nhà máy',
        dataIndex: 'branch',
        width: 100
      },
      {
        title: 'TQL',
        dataIndex: 'manage_group',
        width: 100
      },
      {
        title: 'ĐHT',
        dataIndex: 'parent_device',
        width: 100
      },
      {
        title: 'Mạng lưới',
        dataIndex: 'route',
        width: 100
      },
      {
        title: 'Số Seri',
        dataIndex: 'serial_number',
        width: 100
      },
      {
        title: 'Số ĐT',
        dataIndex: 'phone',
        width: 100
      },
      {
        title: 'Tên ĐH',
        dataIndex: 'device_name',
        width: 100
      },
      {
        title: 'Ngày lắp',
        dataIndex: 'install_time',
        render: (text, record) => {
          return <>{record?.install_time}</>;
        },
        width: 100
      },
      {
        title: 'Cỡ ĐH',
        dataIndex: 'size',
        width: 100
      },
      {
        title: 'Loại ĐH',
        dataIndex: 'type',
        width: 100
      },
      {
        title: 'Kiểu ĐH',
        dataIndex: 'style',
        width: 100
      },
      {
        title: 'Ngày kiểm định',
        dataIndex: 'inspection_date',
        render: (text, record) => {
          return <>{record?.inspection_date}</>;
        },
        width: 150
      },
      {
        title: 'Kinh độ',
        dataIndex: 'longitude',
        width: 100
      },
      {
        title: 'Vĩ độ',
        dataIndex: 'latitude',
        width: 100
      },
      {
        title: 'Thời điểm ĐK',
        dataIndex: 'created_time',
        render: (text, record) => {
          return <>{moment(record?.created_time)?.format('DD/MM/YYYY HH:mm')}</>;
        },
        width: 140
      },
      {
        title: 'Ghi chú',
        dataIndex: 'note',
        width: 150
      },

      {
        title: 'Hành động',
        dataIndex: 'actions',
        width: 100,
        fixed: 'right',
        render: (_, record) => (
          <Space size="middle">
            <Tooltip title="Xem chi tiết">
              <Button
                type="text"
                shape="circle"
                icon={<EyeFilled style={{ color: '#0051A9', fontSize: '18px' }} />}
                onClick={() => handleViewDetail(record)}
              />
            </Tooltip>
            {customer.edit && (
              <Tooltip title="Chỉnh sửa">
                <Button
                  type="text"
                  shape="circle"
                  icon={<EditFilled style={{ color: '#0051A9', fontSize: '18px' }} />}
                  onClick={() => handleEdit(record)}
                />
              </Tooltip>
            )}
            {customer.delete && (
              <Tooltip title="Xoá khách hàng">
                <Button
                  type="danger"
                  shape="circle"
                  icon={<DeleteFilled style={{ color: '#0051A9', fontSize: '18px' }} />}
                  onClick={() => handleDelete(record)}
                />
              </Tooltip>
            )}
          </Space>
        )
      }
    ],
    []
  );

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setFlagSearch(!flagSearch);
  };

  const handleResetSearch = () => {
    setDataFilterAdvance({});
    setSearch('');
    setCurrentPage(1);
    setFlagSearch(!flagSearch);
  };
  const [loading, setLoading] = useState(false);
  const handleExportExcelButtonClick = async () => {
    try {
      setLoading(true);
      await downloadBlobFile(
        `${URL_API}/api/customers/export-report`,
        'Danh sách khách hàng.xlsx',
        {
          keyWord: search ?? undefined,
          status: status === 'all' ? undefined : status,
          ...dataFilterAdvance
        },
        'GET'
      );
    } catch (e) {
      showError('Có lỗi xảy ra, vui lòng liên hệ ban quản trị');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDevice = () => {
    if (!customer.create) {
      showWarning({ message: 'Xin lỗi, bạn không có quyền thực hiện chức năng này' });
      return;
    }
    navigate('/customer/add');
  };

  return (
    <div className="dashboard">
      <ModalImportFile
        visible={visibleModalImport}
        onCancel={() => {
          setVisibleModalImport(false);
          fetchData();
        }}
      />
      <Row justify="space-between" align="middle">
        <Col>
          <Typography.Title level={4} style={{ color: '#0051A9' }}>
            Quản lý khách hàng
          </Typography.Title>
        </Col>
        <Col>
          <Space>
            {customer.create && (
              <Button
                icon={<PlusOutlined />}
                onClick={handleAddDevice}
                style={{
                  color: '#0051A9',
                  height: '36px',
                  backgroundColor: '#E8EBEF',
                  borderColor: '#E8EBEF',
                  fontSize: '18px'
                }}>
                Thêm mới
              </Button>
            )}
            {customer.export && (
              <Button
                icon={<UploadOutlined />}
                onClick={handleExportExcelButtonClick}
                loading={loading}
                style={{
                  color: '#fff',
                  height: '36px',
                  backgroundColor: '#0051A9',
                  borderColor: '#0051A9',
                  fontSize: '18px'
                }}>
                Xuất file excel
              </Button>
            )}
            {customer.import && (
              <Button
                icon={<DownloadOutlined />}
                onClick={() => {
                  setVisibleModalImport(true);
                }}
                style={{
                  color: '#fff',
                  height: '36px',
                  backgroundColor: '#0051A9',
                  borderColor: '#0051A9',
                  fontSize: '18px'
                }}>
                Nhập file excel
              </Button>
            )}
          </Space>
        </Col>
      </Row>
      <Card bodyStyle={{ padding: 8 }}>
        <Row gutter={[8, 8]} style={{ marginTop: 16 }}>
          {/* ===== DÒNG 1: 4 FILTER ===== */}
          <Col xs={24} md={6}>
            <BranchSelectMultiple
              value={dataFilterAdvance?.branch}
              showTitle={false}
              onChange={(values) => setDataFilterAdvance({ ...dataFilterAdvance, branch: values })}
            />
          </Col>

          <Col xs={24} md={6}>
            <ManageGroupSelect
              showTitle={false}
              value={dataFilterAdvance?.manage_group}
              parentBranch={dataFilterAdvance?.branch}
              onChange={(values) =>
                setDataFilterAdvance({ ...dataFilterAdvance, manage_group: values })
              }
            />
          </Col>

          <Col xs={24} md={6}>
            <ParentDeviceSelect
              showTitle={false}
              value={dataFilterAdvance?.parent_device}
              parentBranch={dataFilterAdvance?.branch}
              parentManageGroup={dataFilterAdvance?.manage_group}
              onChange={(values) =>
                setDataFilterAdvance({ ...dataFilterAdvance, parent_device: values })
              }
            />
          </Col>

          <Col xs={24} md={6}>
            <RouteSelect
              showTitle={false}
              value={dataFilterAdvance?.route}
              onChange={(values) => setDataFilterAdvance({ ...dataFilterAdvance, route: values })}
            />
          </Col>

          {/* ===== DÒNG 2: 1 FILTER + SEARCH + BUTTONS ===== */}
          <Col xs={24} md={6}>
            <MeterSizeSelect
              showTitle={false}
              value={dataFilterAdvance?.meter_size}
              onChange={(values) =>
                setDataFilterAdvance({ ...dataFilterAdvance, meter_size: values })
              }
            />
          </Col>

          <Col xs={24} md={10}>
            <Input
              value={search}
              onChange={handleSearchChange}
              placeholder="Tìm kiếm tên, mã, số serial, địa chỉ..."
              onPressEnter={handleSearch}
              style={{ width: '100%', height: 36 }}
            />
          </Col>

          <Col xs={24} md={8}>
            <Space>
              <Button onClick={() => navigate('/favorite-filters')} style={{ height: 36 }}>
                ⭐ Bộ lọc theo dõi
              </Button>
              <Button icon={<ClearOutlined />} onClick={handleResetSearch} style={{ height: 36 }}>
                Xoá lọc
              </Button>

              <Button
                icon={<SearchOutlined />}
                onClick={handleSearch}
                style={{
                  height: 36,
                  backgroundColor: '#0051A9',
                  borderColor: '#0051A9',
                  color: '#fff'
                }}>
                Tìm kiếm
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card bodyStyle={{ padding: 0 }}>
        <Tabs
          style={{ backgroundColor: '#fff', borderRadius: 0 }}
          activeKey={status}
          defaultActiveKey="all"
          onChange={(value) => {
            setCurrentPage(1);
            setStatus(value);
          }}>
          <TabPane tab={`Tất cả`} key={'all'}>
            <Table
              bordered
              // size="middle"
              loading={isFetching}
              dataSource={deviceList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: 0,
                thead: { backgroundColor: '#DBDFE5', color: '#626D7C' },
                fontSize: '30px'
              }}
              scroll={{ x: 'max-content', y: 600 }}
            />
          </TabPane>
          <TabPane tab={`Đang lắp đặt`} key={'IN_USE'}>
            <Table
              bordered
              // size="middle"
              loading={isFetching}
              dataSource={deviceList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: 0,
                thead: { backgroundColor: '#DBDFE5', color: '#626D7C' },
                fontSize: '30px'
              }}
              scroll={{ x: 'max-content', y: 600 }}
            />
          </TabPane>
          <TabPane tab={`Không lắp đặt`} key={'STORED'}>
            <Table
              bordered
              // size="middle"
              loading={isFetching}
              dataSource={deviceList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: 0,
                thead: { backgroundColor: '#DBDFE5', color: '#626D7C' },
                fontSize: '30px'
              }}
              scroll={{ x: 'max-content', y: 600 }}
            />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
}
